import {
  Component,
  EventEmitter,
  Output,
  Input,
  signal,
  effect,
  OnDestroy,
  inject
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileLoader } from '../../services/document-viewer/document-viewer.service';
import { DocumentModificationService } from '../../services/document-modification/document-modification.service';
import { ToastService } from '../../services/toast-messages/toast-messages.service';
import { ThumbnailViewerComponent } from '../thumbnail-viewer/thumbnail-viewer.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Bind } from 'primeng/bind';
import { RadioButton } from 'primeng/radiobutton';
import { FormsModule } from '@angular/forms';
import { Divider } from 'primeng/divider';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-document-split',
  templateUrl: './document-split.component.html',
  styleUrl: './document-split.component.scss',
  providers: [FileLoader],
  imports: [
    ThumbnailViewerComponent,
    CdkScrollable,
    Bind,
    RadioButton,
    FormsModule,
    Divider,
    Button,
    Dialog,
    TranslatePipe
  ]
})
export class DocumentSplitComponent implements OnDestroy {
  private fileLoader = inject(FileLoader);
  private documentModificationService = inject(DocumentModificationService);
  private toastService = inject(ToastService);

  /**
   *
   */
  @Input()
  set base64Content(value: string) {
    this.documentContent.set(value);
  }
  @Input() fileName: string = '';
  @Input() documentId!: string;
  @Input() tenantId!: string;
  @Output() closeSplitEvent = new EventEmitter<void>();
  @Output() documentSplitEvent = new EventEmitter<any>();

  private destroy$ = new Subject<void>();
  private documentContent = signal('');
  private splitCounter: number = 0;
  thumbnails = signal<string[]>([]);
  selectedPages: number[] = [];
  selectedDocumentPages: number[] = [];
  rangeSettings: string = 'Selected Pages';
  modeSettings: string = 'Merge Pages';
  isDialogVisible: boolean = false;
  isLoading: boolean = true;

  constructor() {
    effect(
      () => {
        if (this.documentContent()) {
          this.loadThumbnails();
        }
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Toggles the selection state of a page by adding or removing it from selectedPages array.
   * @param pageIndex The zero-based index of the page to toggle
   */
  togglePageSelection(pageIndex: number) {
    if (this.selectedPages.includes(pageIndex)) {
      this.selectedPages = this.selectedPages.filter(p => p !== pageIndex);
      if (this.rangeSettings === 'All Pages') {
        this.rangeSettings = 'Selected Pages';
      }
    } else {
      this.selectedPages = [...this.selectedPages, pageIndex];
    }
  }

  /**
   * Updates the selectedDocumentPages array when the document pages selection changes.
   * @param documentPages The array of selected document page indices.
   */
  onSelectedDocumentPagesChange(documentPages: number[]) {
    this.selectedDocumentPages = documentPages;
  }

  /**
   * Handles range setting changes, selecting all pages or clearing selection based on the value.
   * @param value The range setting value ('All Pages' or 'Selected Pages')
   */
  onRangeChange(value: string) {
    if (value === 'All Pages') {
      this.selectedPages = Array.from({ length: this.thumbnails().length }, (_, i) => i);
      this.selectedDocumentPages = Array.from({ length: this.thumbnails().length }, (_, i) => i);
    } else if (value === 'Selected Pages') {
      this.selectedPages = [];
      this.selectedDocumentPages = [];
    }
  }

  /**
   * Opens the split confirmation dialog
   */
  openSplitDialog() {
    this.isDialogVisible = true;
  }

  /**
   * Closes the split confirmation dialog
   */
  closeSplitDialog() {
    this.isDialogVisible = false;
  }

  /**
   * Initial load and regenerates thumbnails from updated base64Content
   */
  async loadThumbnails() {
    this.isLoading = true;
    if (this.documentContent()) {
      try {
        const newThumbnails = await this.fileLoader.generateAllThumbnails(this.documentContent());
        this.thumbnails.set(newThumbnails);
        this.selectedPages = [];
      } catch (error) {
        this.toastService.error('Thumbnails Load Failed', 'Unable to load thumbnails.');
      } finally {
        this.isLoading = false;
      }
    }
  }

  /**
   * Splits the document by extracting selected pages into a new document.
   */
  splitDocument() {
    if (this.selectedDocumentPages.length === 0) return;

    this.closeSplitDialog();

    const pageMapping = this.selectedPages.map((visualPos, index) => ({
      visualPos,
      documentPage: this.selectedDocumentPages[index]
    }));

    const sortedDocumentPages = pageMapping
      .sort((a, b) => a.visualPos - b.visualPos)
      .map(item => item.documentPage);

    const request = {
      documentId: this.documentId,
      pageNumbers: sortedDocumentPages.map(pageNum => pageNum + 1),
      newDocumentFileName: `${this.fileName.split('.')[0]}_split ${this.splitCounter + 1}.pdf`
    };

    this.documentModificationService
      .splitDocument(this.tenantId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.splitCounter++;
          this.documentSplitEvent.emit(response);
          this.toastService.success(
            'documentSplit.splitSuccess',
            'documentSplit.successfulSplitMessage'
          );
        },
        error: error => {
          this.toastService.error('documentSplit.splitFailed', 'documentSplit.splitFailedMessage');
        }
      });
  }

  /**
   * Emits the closeSplitEvent to notify parent component to close the split view.
   */
  closeSplit() {
    this.closeSplitEvent.emit();
  }

  /**
   *
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
